'use client'

import { cn } from '@brew/ui/cn'
import { ArrowUpRight } from 'lucide-react'
import Link from 'next/link'
import type { ReactNode } from 'react'
import { HomepagePromptCollector } from '@/components/homepage-prompt-collector'
import { HeroGlow } from './hero-glow'
import { MarketingContainer } from './marketing-container'
import { MARKETING_GUTTER_X, MARKETING_HOME_HERO_Y } from './marketing-layout'
import { MediaPlaceholder } from './media-placeholder'
import { PillButton } from './pill-button'

/**
 * Home hero headline scale. Intentionally one step smaller than the
 * shared `MARKETING_HERO_HEADING` ladder (which is reserved for
 * `ArticleHero` / `EditorialDivider`). Cursor.com's landing hero reads
 * tighter than its closing pull-quote, so we mirror that on `/`.
 */
const HOME_HERO_HEADING = 'marketing-display text-3xl md:text-5xl lg:text-6xl'

export type HeroAnnouncement = {
  label: string
  href?: string
}

const ANNOUNCEMENT_CLASSES =
  'inline-flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/[0.06] px-3.5 py-1.5 text-xs font-medium text-foreground/80 dark:bg-primary/10'

function AnnouncementChip({
  announcement,
}: {
  announcement: HeroAnnouncement
}) {
  const dot = (
    <span
      aria-hidden="true"
      className="size-1.5 rounded-full bg-primary shadow-[0_0_0_3px_color-mix(in_srgb,var(--primary),transparent_82%)]"
    />
  )

  if (announcement.href) {
    return (
      <Link
        href={announcement.href}
        className={`${ANNOUNCEMENT_CLASSES} transition-colors hover:border-primary/40 hover:text-primary`}
      >
        {dot}
        {announcement.label}
        <ArrowUpRight className="size-3.5 text-primary opacity-80" />
      </Link>
    )
  }

  return (
    <span className={ANNOUNCEMENT_CLASSES}>
      {dot}
      {announcement.label}
    </span>
  )
}

export type HeroV2Props = {
  announcement?: HeroAnnouncement
  headline: ReactNode
  subheadline?: ReactNode
  /** Replaces the default `HomepagePromptCollector` composer slot. */
  composer?: ReactNode
  /** Secondary CTA shown to the right of the composer (e.g. "Watch demo"). */
  secondaryCtaLabel?: string
  secondaryCtaHref?: string
  /** Below-the-composer media slot. Defaults to a 16/9 `<MediaPlaceholder>`. */
  visual?: ReactNode
  className?: string
}

/**
 * The cursor.com-style editorial hero. Home page only.
 */
export function HeroV2({
  announcement,
  headline,
  subheadline,
  composer,
  secondaryCtaLabel,
  secondaryCtaHref,
  visual,
  className,
}: HeroV2Props) {
  return (
    <section
      className={cn(
        'relative isolate w-full overflow-hidden',
        MARKETING_GUTTER_X,
        MARKETING_HOME_HERO_Y,
        className
      )}
    >
      <HeroGlow />

      <div className="relative z-10">
        <MarketingContainer
          size="narrow"
          className="flex flex-col items-center gap-6 text-center md:gap-7"
        >
          {announcement ? (
            <AnnouncementChip announcement={announcement} />
          ) : null}

          <div className="flex max-w-4xl flex-col items-center gap-3 md:gap-4">
            <h1 className={cn(HOME_HERO_HEADING, 'text-foreground')}>
              {headline}
            </h1>

            {subheadline ? (
              <p className="max-w-2xl text-base text-muted-foreground md:text-lg">
                {subheadline}
              </p>
            ) : null}
          </div>

          <div className="flex w-full flex-col items-center gap-3 md:gap-4">
            <div className="w-full max-w-2xl text-left">
              {composer ?? <HomepagePromptCollector />}
            </div>
            {secondaryCtaLabel && secondaryCtaHref ? (
              <PillButton variant="ghost" href={secondaryCtaHref}>
                {secondaryCtaLabel}
              </PillButton>
            ) : null}
          </div>
        </MarketingContainer>

        <MarketingContainer size="visual" className="mt-10 md:mt-12 lg:mt-14">
          {visual ?? (
            <MediaPlaceholder
              aspect="16/9"
              label="Brew in action"
              hint="Hero product GIF / video, to be added"
            />
          )}
        </MarketingContainer>
      </div>
    </section>
  )
}
